import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, Info, Loader2 } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { toast } from "../components/ui/sonner";
import {
  AGE_RANGES, ARCHETYPES, OCCASIONS, COMFORT, SKIN_SWATCHES,
  UNDERTONE_QUIZ, scoreUndertone, QUICK_MEASUREMENTS, ADVANCED_MEASUREMENTS,
} from "../data/styleData";

const STEPS = ["The basics", "Your measurements", "Skin & color", "Style & life"];

function Chip({ selected, onClick, children, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`text-left px-5 py-3 rounded-full border transition-colors duration-300 ${
        selected ? "bg-emerald text-white border-emerald" : "bg-transparent border-ink/25 text-ink hover:border-emerald"
      }`}
    >
      {children}
    </button>
  );
}

export default function Onboarding() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);

  // basics
  const [ageRange, setAgeRange] = useState("");
  const [unit, setUnit] = useState("imperial");
  const [feet, setFeet] = useState("");
  const [inches, setInches] = useState("");
  const [heightCm, setHeightCm] = useState("");
  const [weight, setWeight] = useState("");
  const [dressSize, setDressSize] = useState("");

  // measurements
  const [tier, setTier] = useState("quick");
  const [mvals, setMvals] = useState({});
  const [openGuide, setOpenGuide] = useState(null);

  // color
  const [depth, setDepth] = useState("");
  const [answers, setAnswers] = useState({});

  // style
  const [archetypes, setArchetypes] = useState([]);
  const [occasions, setOccasions] = useState([]);
  const [comfort, setComfort] = useState([]);

  const toggle = (list, setList, val, max) => {
    if (list.includes(val)) setList(list.filter((x) => x !== val));
    else if (!max || list.length < max) setList([...list, val]);
  };

  const heightValue = () => {
    if (unit === "imperial") {
      const f = parseFloat(feet) || 0;
      const i = parseFloat(inches) || 0;
      return f || i ? f * 12 + i : null;
    }
    return parseFloat(heightCm) || null;
  };

  const saveBasics = () =>
    api.put("/profile/basics", {
      age_range: ageRange,
      unit,
      height: heightValue(),
      weight: parseFloat(weight) || null,
      dress_size: dressSize || null,
    });

  const saveMeasurements = () => {
    const values = {};
    Object.entries(mvals).forEach(([k, v]) => {
      const n = parseFloat(v);
      if (!isNaN(n) && n > 0) values[k] = n;
    });
    return api.put("/profile/measurements", { tier, unit, values });
  };

  const saveColor = () => {
    const ut = scoreUndertone(answers);
    const depthBucket = depth || "deep";
    return api.put("/profile/color", { depth: depthBucket, undertone: ut, quiz_answers: answers });
  };

  const saveStyle = () => api.put("/profile/style", { archetypes, occasions, comfort });

  const canProceed = () => {
    if (step === 0) return ageRange && heightValue();
    if (step === 2) return depth && Object.keys(answers).length === UNDERTONE_QUIZ.length;
    if (step === 3) return archetypes.length > 0 && occasions.length > 0;
    return true; // measurements optional beyond quick
  };

  const next = async () => {
    setSaving(true);
    try {
      if (step === 0) await saveBasics();
      if (step === 1) await saveMeasurements();
      if (step === 2) await saveColor();
      if (step === 3) {
        await saveStyle();
        toast.success("Your profile is ready.");
        navigate("/dashboard");
        return;
      }
      setStep((s) => s + 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const unitLabel = unit === "imperial" ? "in" : "cm";

  return (
    <div className="min-h-screen bg-sand text-ink">
      <header className="px-6 sm:px-10 lg:px-16 py-6 flex items-center justify-between">
        <div className="font-serif text-2xl">Élanora</div>
        <div className="text-sm text-ink/60">Step {step + 1} of {STEPS.length}</div>
      </header>

      {/* progress */}
      <div className="px-6 sm:px-10 lg:px-16">
        <div className="h-[3px] bg-ink/10 rounded-full overflow-hidden">
          <div className="h-full bg-emerald transition-all duration-500" style={{ width: `${((step + 1) / STEPS.length) * 100}%` }} />
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-14">
        <p className="eyebrow mb-3">{STEPS[step]}</p>

        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.4 }}
          >
            {/* STEP 0 - BASICS */}
            {step === 0 && (
              <div className="space-y-12">
                <h1 className="text-4xl sm:text-5xl tracking-tight">Let's start with a little about you.</h1>

                <div>
                  <label className="block text-lg mb-4">Your age range</label>
                  <div className="flex flex-wrap gap-3">
                    {AGE_RANGES.map((a) => (
                      <Chip key={a} selected={ageRange === a} onClick={() => setAgeRange(a)} testid={`age-${a}`}>{a}</Chip>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-lg mb-4">Measurement units</label>
                  <div className="inline-flex border border-ink/25 rounded-full p-1">
                    {["imperial", "metric"].map((u) => (
                      <button
                        key={u} type="button" onClick={() => setUnit(u)} data-testid={`unit-${u}`}
                        className={`px-6 py-2 rounded-full text-sm capitalize transition-colors duration-300 ${unit === u ? "bg-emerald text-white" : "text-ink/70"}`}
                      >
                        {u === "imperial" ? "Imperial (in/lb)" : "Metric (cm/kg)"}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-8">
                  <div>
                    <label className="block text-lg mb-4">Height</label>
                    {unit === "imperial" ? (
                      <div className="flex gap-4">
                        <input type="number" placeholder="ft" value={feet} onChange={(e) => setFeet(e.target.value)} data-testid="height-feet"
                          className="w-24 bg-transparent border-b border-ink/40 focus:border-emerald outline-none py-3 text-lg" />
                        <input type="number" placeholder="in" value={inches} onChange={(e) => setInches(e.target.value)} data-testid="height-inches"
                          className="w-24 bg-transparent border-b border-ink/40 focus:border-emerald outline-none py-3 text-lg" />
                      </div>
                    ) : (
                      <input type="number" placeholder="cm" value={heightCm} onChange={(e) => setHeightCm(e.target.value)} data-testid="height-cm"
                        className="w-40 bg-transparent border-b border-ink/40 focus:border-emerald outline-none py-3 text-lg" />
                    )}
                  </div>
                  <div>
                    <label className="block text-lg mb-4">Weight <span className="text-ink/40 text-sm">(optional)</span></label>
                    <input type="number" placeholder={unit === "imperial" ? "lb" : "kg"} value={weight} onChange={(e) => setWeight(e.target.value)} data-testid="weight-input"
                      className="w-40 bg-transparent border-b border-ink/40 focus:border-emerald outline-none py-3 text-lg" />
                  </div>
                </div>

                <div>
                  <label className="block text-lg mb-4">Usual dress size <span className="text-ink/40 text-sm">(optional)</span></label>
                  <input type="text" placeholder="e.g. 14, 16W, XL" value={dressSize} onChange={(e) => setDressSize(e.target.value)} data-testid="dress-size"
                    className="w-56 bg-transparent border-b border-ink/40 focus:border-emerald outline-none py-3 text-lg" />
                </div>
              </div>
            )}

            {/* STEP 1 - MEASUREMENTS */}
            {step === 1 && (
              <div className="space-y-10">
                <div>
                  <h1 className="text-4xl sm:text-5xl tracking-tight">Your measurements.</h1>
                  <p className="mt-4 text-lg text-ink/70">Fit comes before size here. Share what you're comfortable with — everything beyond the quick five is optional, and our engine adapts to whatever you give it.</p>
                </div>

                <div className="inline-flex border border-ink/25 rounded-full p-1">
                  {[["quick", "Quick — 5"], ["advanced", "Advanced — 13"]].map(([v, l]) => (
                    <button key={v} type="button" onClick={() => setTier(v)} data-testid={`tier-${v}`}
                      className={`px-6 py-2 rounded-full text-sm transition-colors duration-300 ${tier === v ? "bg-emerald text-white" : "text-ink/70"}`}>{l}</button>
                  ))}
                </div>

                <div className="space-y-6">
                  {[...QUICK_MEASUREMENTS, ...(tier === "advanced" ? ADVANCED_MEASUREMENTS : [])].map((m) => (
                    <div key={m.key} className="border-b border-ink/10 pb-5">
                      <div className="flex items-center justify-between gap-4">
                        <label className="text-lg">{m.label}</label>
                        <div className="flex items-center gap-3">
                          <input type="number" placeholder={unitLabel} value={mvals[m.key] || ""}
                            onChange={(e) => setMvals({ ...mvals, [m.key]: e.target.value })}
                            data-testid={`m-${m.key}`}
                            className="w-28 bg-transparent border-b border-ink/40 focus:border-emerald outline-none py-2 text-lg text-right" />
                          <button type="button" onClick={() => setOpenGuide(openGuide === m.key ? null : m.key)} data-testid={`guide-${m.key}`}
                            className="text-emerald hover:opacity-70 transition-opacity duration-300">
                            <Info className="w-5 h-5" strokeWidth={1.5} />
                          </button>
                        </div>
                      </div>
                      {openGuide === m.key && (
                        <motion.p initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }}
                          className="mt-3 text-sm text-ink/60 bg-[#F2EFEB] p-4 rounded-[2px]">{m.guide}</motion.p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* STEP 2 - SKIN & COLOR */}
            {step === 2 && (
              <div className="space-y-12">
                <div>
                  <h1 className="text-4xl sm:text-5xl tracking-tight">Your skin, your colors.</h1>
                  <p className="mt-4 text-lg text-ink/70">Choose the swatch closest to your skin in natural light, then answer four quick questions. We'll build a palette made for you.</p>
                </div>

                <div>
                  <label className="block text-lg mb-4">Closest match to your skin</label>
                  <div className="grid grid-cols-5 gap-3">
                    {SKIN_SWATCHES.map((s) => (
                      <button key={s.hex} type="button" onClick={() => setDepth(s.depth)} data-testid={`skin-${s.label}`}
                        className={`group relative aspect-square rounded-[2px] border-2 transition-all duration-300 ${depth === s.depth ? "border-emerald scale-105" : "border-transparent hover:border-ink/30"}`}
                        style={{ background: s.hex }}>
                        {depth === s.depth && <Check className="w-5 h-5 text-white absolute inset-0 m-auto" strokeWidth={2} />}
                      </button>
                    ))}
                  </div>
                  <p className="text-sm text-ink/50 mt-3">Every depth is beautiful — this simply tunes contrast and saturation.</p>
                </div>

                <div className="space-y-8">
                  {UNDERTONE_QUIZ.map((q) => (
                    <div key={q.id}>
                      <label className="block text-lg mb-4">{q.q}</label>
                      <div className="flex flex-wrap gap-3">
                        {q.options.map((o) => (
                          <Chip key={o.label} selected={answers[q.id] === o.value}
                            onClick={() => setAnswers({ ...answers, [q.id]: o.value })}
                            testid={`ut-${q.id}-${o.value}`}>{o.label}</Chip>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* STEP 3 - STYLE */}
            {step === 3 && (
              <div className="space-y-12">
                <div>
                  <h1 className="text-4xl sm:text-5xl tracking-tight">Your style & your life.</h1>
                  <p className="mt-4 text-lg text-ink/70">Tell us how you love to dress and the rooms you walk into.</p>
                </div>

                <div>
                  <label className="block text-lg mb-2">Which describe your style? <span className="text-ink/40 text-sm">(pick up to 5)</span></label>
                  <div className="grid sm:grid-cols-2 gap-3 mt-4">
                    {ARCHETYPES.map((a) => (
                      <button key={a.name} type="button" onClick={() => toggle(archetypes, setArchetypes, a.name, 5)} data-testid={`arch-${a.name}`}
                        className={`text-left p-4 rounded-[2px] border transition-colors duration-300 ${archetypes.includes(a.name) ? "bg-emerald text-white border-emerald" : "border-ink/20 hover:border-emerald"}`}>
                        <span className="block font-medium">{a.name}</span>
                        <span className={`block text-sm mt-1 ${archetypes.includes(a.name) ? "text-white/70" : "text-ink/55"}`}>{a.desc}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-lg mb-4">Occasions you dress for</label>
                  <div className="flex flex-wrap gap-3">
                    {OCCASIONS.map((o) => (
                      <Chip key={o} selected={occasions.includes(o)} onClick={() => toggle(occasions, setOccasions, o)} testid={`occ-${o}`}>{o}</Chip>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-lg mb-2">Comfort preferences</label>
                  <p className="text-sm text-ink/50 mb-4">All framed the way we style — around what you love, never what to hide.</p>
                  <div className="flex flex-wrap gap-3">
                    {COMFORT.map((c) => (
                      <Chip key={c} selected={comfort.includes(c)} onClick={() => toggle(comfort, setComfort, c)} testid={`comfort-${c}`}>{c}</Chip>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        {/* nav */}
        <div className="flex items-center justify-between mt-16">
          <button
            type="button"
            onClick={() => { if (step > 0) setStep((s) => s - 1); else navigate("/"); }}
            data-testid="onboarding-back"
            className="inline-flex items-center gap-2 text-ink/60 hover:text-ink transition-colors duration-300"
          >
            <ArrowLeft className="w-5 h-5" strokeWidth={1.5} /> Back
          </button>
          <button
            type="button"
            onClick={next}
            disabled={!canProceed() || saving}
            data-testid="onboarding-next"
            className="inline-flex items-center gap-3 rounded-full bg-emerald text-white px-8 py-4 hover:bg-emerald/90 transition-colors duration-300 disabled:opacity-40"
          >
            {saving ? <Loader2 className="w-5 h-5 animate-spin" strokeWidth={1.5} /> : null}
            {step === STEPS.length - 1 ? "See my palette" : "Continue"}
            {!saving && <ArrowRight className="w-5 h-5" strokeWidth={1.5} />}
          </button>
        </div>
      </div>
    </div>
  );
}
